import { GlobalStore } from "@roc/feature-app-core";
import { PayoffService } from "../services/payoffService";
import { action, flow, makeObservable, observable } from "mobx";
import { DocumentStore } from "@roc/feature-documents";
import { LoanStore } from "@roc/feature-loans";
import PayoffFieldsFormStore from "./payoffFieldsFormStore";
import PayoffAdjustmentsFormStore from "./payoffAdjustmentsFormStore";
import { downloadDocument } from "@roc/feature-utils";

export class PayoffCalculatorStore {
  private documentStore: DocumentStore;
  private globalStore: GlobalStore;
  loanStore: LoanStore;
  payoffFieldsFormStore: PayoffFieldsFormStore;
  payoffAdjustmentsFormStore: PayoffAdjustmentsFormStore;
  payoffService: PayoffService;

  isEditPayoffFieldsDialogOpen: boolean;
  isEditPayoffAdjustmentsDialogOpen: boolean;
  isConfirmReCalculateDialogOpen: boolean;
  isProtectiveAdvancesMoreInfoDialogOpen: boolean;
  unpaidInterestTotal: number;
  latePenaltyTotal: number;
  defaultInterestTotal: number;
  maturityDefaultTotal: number;
  payoffCalculatorData: any;
  servicerFees: Array<any>;
  protectiveAdvances: Array<any>;
  finalTotals: Array<any>;
  selectedView: string;
  aggregatedServicerFees: number;
  aggregatedUnpaidCharges: number;
  payoffAmount: number;
  calculatorAmount: number;
  finalTotal: number;
  unpaidInterestAggregated: number;
  isPrePayoffCalculator: boolean;
  dateForPrePayoffCalculator: string;

  constructor(
    loanStore: LoanStore,
    documentStore: DocumentStore,
    globalStore: GlobalStore
  ) {
    this.documentStore = documentStore;
    this.globalStore = globalStore;
    this.loanStore = loanStore;
    this.payoffFieldsFormStore = new PayoffFieldsFormStore(this.globalStore);
    this.payoffAdjustmentsFormStore = new PayoffAdjustmentsFormStore(this.globalStore);
    this.payoffService = new PayoffService();
    this.isPrePayoffCalculator = false;
    this.setDefaults();
    makeObservable(this, {
      initialize: flow,
      isEditPayoffFieldsDialogOpen: observable,
      isEditPayoffAdjustmentsDialogOpen: observable,
      isConfirmReCalculateDialogOpen: observable,
      setIsEditPayoffFieldsDialogOpen: action,
      setIsEditPayoffAdjustmentsDialogOpen: action,
      setIsConfirmReCalculateDialogOpen: action,
      unpaidInterestTotal: observable,
      latePenaltyTotal: observable,
      defaultInterestTotal: observable,
      maturityDefaultTotal: observable,
      savePayoffFields: flow,
      savePayoffAdjustments: flow,
      downloadCalculatorPDF: flow,
      payoffRecalculate: flow,
      payoffCalculatorData: observable,
      getPayoffCalculatorData: flow,
      getPrePayoffCalculatorData: flow,
      servicerFees: observable,
      setServicerFees: action,
      protectiveAdvances: observable,
      setProtectiveAdvances: action,
      finalTotals: observable,
      setFinalTotals: action,
      setPayoffCalculatorTotalValues: action,
      selectedView: observable,
      setSelectedView: action,
      aggregatedServicerFees: observable,
      aggregatedUnpaidCharges: observable,
      getAggregatedServicerFees: action,
      getAggregatedUnpaidCharges: action,
      payoffAmount: observable,
      getPayoffAmount: action,
      calculatorAmount: observable,
      getCalculatorAmount: action,
      finalTotal: observable,
      getFinalTotal: action,
      unpaidInterestAggregated: observable,
      getUnpaidInterestAggregated: action,
      downloadPayoffExcel: flow,
      isProtectiveAdvancesMoreInfoDialogOpen: observable,
      setIsProtectiveAdvancesMoreInfoDialogOpen: action,
      isPrePayoffCalculator: observable,
      setIsPrePayoffCalculator: action,
      dateForPrePayoffCalculator: observable,
      setDateForPrePayoffCalculator: action,
    });
  }

  setDefaults() {
    this.isEditPayoffFieldsDialogOpen = false;
    this.isEditPayoffAdjustmentsDialogOpen = false;
    this.isConfirmReCalculateDialogOpen = false;
    this.isProtectiveAdvancesMoreInfoDialogOpen = false;
    this.unpaidInterestTotal = 0;
    this.latePenaltyTotal = 0;
    this.defaultInterestTotal = 0;
    this.maturityDefaultTotal = 0;
    this.payoffCalculatorData = {};
    this.servicerFees = [];
    this.protectiveAdvances = [];
    this.finalTotals = [];
    this.selectedView = 'compactTableView';
    this.aggregatedServicerFees = 0;
    this.aggregatedUnpaidCharges = 0;
    this.payoffAmount = 0;
    this.calculatorAmount = 0;
    this.finalTotal = 0;
    this.unpaidInterestAggregated = 0;
  }

  setIsEditPayoffFieldsDialogOpen(isEditPayoffFieldsDialogOpen: boolean) {
    this.isEditPayoffFieldsDialogOpen = isEditPayoffFieldsDialogOpen;
  }

  setIsEditPayoffAdjustmentsDialogOpen(isEditPayoffAdjustmentsDialogOpen: boolean) {
    this.isEditPayoffAdjustmentsDialogOpen = isEditPayoffAdjustmentsDialogOpen;
  }

  setIsConfirmReCalculateDialogOpen(isConfirmReCalculateDialogOpen: boolean) {
    this.isConfirmReCalculateDialogOpen = isConfirmReCalculateDialogOpen;
  }

  setIsProtectiveAdvancesMoreInfoDialogOpen(isProtectiveAdvancesMoreInfoDialogOpen: boolean) {
    this.isProtectiveAdvancesMoreInfoDialogOpen = isProtectiveAdvancesMoreInfoDialogOpen;
  }

  setSelectedView(selectedView) {
    this.selectedView = selectedView;
  }

  reset() {
    this.setDefaults();
    this.payoffFieldsFormStore.reset();
    this.payoffAdjustmentsFormStore.reset();
  }

  *initialize() {
    this.reset();
    if (this.isPrePayoffCalculator) {
      this.getPrePayoffCalculatorData();
    } else {
      this.getPayoffCalculatorData();
    }
  }

  *getPayoffCalculatorData() {
    try {
      const response = yield this.payoffService.getPayoffCalculatorData(this.loanStore?.loanDetails?.loanId, this.documentStore?.currentDocument?.payoffId);
      this.payoffCalculatorData = response?.data;
      this.payoffFieldsFormStore.loadForm(this.payoffCalculatorData?.loanPayoffData);
      this.payoffAdjustmentsFormStore.loadForm(this.payoffCalculatorData?.revenueAdjustment);
      this.setServicerFees(response?.data);
      this.setProtectiveAdvances(response?.data);
      this.calculateAndSetCalculatorTotals();
      this.setFinalTotals();
    } catch (e) {
      console.log(e);
      this.globalStore.notificationStore.showErrorNotification({
        message: 'An error occurred while getting payoff calculator',
      });
    }
  }

  *getPrePayoffCalculatorData() {
    try {
      const response = yield this.payoffService.getPrePayoffCalculatorData(this.loanStore?.loanDetails?.loanId, this.dateForPrePayoffCalculator);
      this.payoffCalculatorData = response?.data;
      this.payoffFieldsFormStore.loadForm(this.payoffCalculatorData?.loanPayoffData);
      this.payoffAdjustmentsFormStore.loadForm(this.payoffCalculatorData?.revenueAdjustment);
      this.setServicerFees(response?.data);
      this.setProtectiveAdvances(response?.data);
      this.calculateAndSetCalculatorTotals();
      this.setFinalTotals();
    } catch (e) {
      console.log(e);
      this.globalStore.notificationStore.showErrorNotification({
        message: 'An error occurred while getting pre payoff calculator',
      });
    }
  }

  setServicerFees(data) {
    this.servicerFees = data?.servicerFees?.map(servicerFee => ({
      ...servicerFee,
      name: servicerFee?.description,
      amount: servicerFee?.origAmt,
    }));
  }

  setProtectiveAdvances(data) {
    this.protectiveAdvances = data?.servicingTransactions?.map(servicingTransaction => ({
      ...servicingTransaction,
      name: servicingTransaction?.invoice,
      type: servicingTransaction?.feeType,
      amount: servicingTransaction?.amount,
    }));
  }

  setFinalTotals() {
    try {
      this.finalTotals = [
        {
          name: 'Unpaid Balance',
          amount: this.payoffCalculatorData?.fundedAmount,
        },
        {
          name: 'Payoff Summary',
          amount: this.payoffAmount,
        },
        {
          name: 'Unpaid Interest',
          amount: this.calculatorAmount,
        },
        {
          name: 'Servicer Fees',
          amount: this.aggregatedServicerFees,
        },
        {
          name: 'Unpaid Charges',
          amount: this.aggregatedUnpaidCharges,
        },
      ];
    } catch (e) {
      console.log(e);
      this.globalStore.notificationStore.showErrorNotification({
        message: 'An error occurred while getting final totals.',
      });
    }
  }

  setPayoffCalculatorTotalValues() {
    this.unpaidInterestTotal = this.unpaidInterestAggregated + this.payoffCalculatorData?.revenueAdjustment?.interestDue;
    this.latePenaltyTotal = this.payoffCalculatorData?.latePenalty + this.payoffCalculatorData?.revenueAdjustment?.latePenaltyDue;
    this.defaultInterestTotal = this.payoffCalculatorData?.defaultPenalty + this.payoffCalculatorData?.revenueAdjustment?.defaultPenaltyDue;
    this.maturityDefaultTotal = this.payoffCalculatorData?.maturityPenalty + this.payoffCalculatorData?.revenueAdjustment?.maturityPenaltyDue;
  }

  getAggregatedServicerFees() {
    let aggregatedServicerFeesTemp = 0;

    this.servicerFees.forEach(row => {
      aggregatedServicerFeesTemp += row?.amount;
    });
    return aggregatedServicerFeesTemp;
  }

  getAggregatedUnpaidCharges() {
    let aggregatedUnpaidChargesTemp = 0;

    this.protectiveAdvances.forEach(row => {
      aggregatedUnpaidChargesTemp += row?.amount;
    });
    return aggregatedUnpaidChargesTemp;
  }

  getCalculatorAmount() {
    return this.unpaidInterestTotal + this.latePenaltyTotal + this.defaultInterestTotal + this.maturityDefaultTotal;
  }

  getPayoffAmount() {
    return this.payoffCalculatorData?.reserveFees + this.payoffCalculatorData?.toChargesPrincipal
      + this.payoffCalculatorData?.loanPayoffData?.prepaymentPenalty + this.payoffCalculatorData?.loanPayoffData?.waiverAmount
      + this.payoffCalculatorData?.loanPayoffData?.otherFeesAdjustment;
  }

  getFinalTotal() {
    return this.aggregatedServicerFees + this.aggregatedUnpaidCharges + this.payoffAmount + this.calculatorAmount + this.payoffCalculatorData?.fundedAmount;
  }

  getUnpaidInterestAggregated() {
    return this.payoffCalculatorData?.unpaidInterest + this.payoffCalculatorData?.accruedInterest;
  }

  calculateAndSetCalculatorTotals() {
    this.aggregatedServicerFees = this.getAggregatedServicerFees();
    this.aggregatedUnpaidCharges = this.getAggregatedUnpaidCharges();
    this.unpaidInterestAggregated = this.getUnpaidInterestAggregated();
    this.setPayoffCalculatorTotalValues();
    this.payoffAmount = this.getPayoffAmount();
    this.calculatorAmount = this.getCalculatorAmount();
    this.finalTotal = this.getFinalTotal();
  }

  *savePayoffFields() {
    try {
      const data = {
        loanId: this.payoffCalculatorData?.loanPayoffData?.loanId,
        loanPayoffDataId: this.payoffCalculatorData?.loanPayoffData?.loanPayoffDataId,
        ...this.payoffFieldsFormStore.getFormValues(),
      };
      const response = yield this.payoffService.savePayoffData(data);
      yield this.getPayoffCalculatorData();
      this.globalStore.notificationStore.showSuccessNotification({
        message: 'Payoff fields saved successfully.',
      });
      this.setIsEditPayoffFieldsDialogOpen(false);
    } catch (e) {
      console.log(e);
      this.globalStore.notificationStore.showErrorNotification({
        message: 'An error occurred while saving payoff fields.',
      });
    }
  }

  *savePayoffAdjustments() {
    try {
      const data = {
        loanId: this.payoffCalculatorData?.revenueAdjustment?.loanId,
        loanRevenueDataId: this.payoffCalculatorData?.revenueAdjustment?.loanRevenueDataId,
        ...this.payoffAdjustmentsFormStore.getFormValues(),
      };
      const response = yield this.payoffService.saveRevenueAdjustments(data);
      yield this.getPayoffCalculatorData();
      this.globalStore.notificationStore.showSuccessNotification({
        message: 'Payoff adjustments saved successfully.',
      });
      this.setIsEditPayoffAdjustmentsDialogOpen(false);
    } catch (e) {
      console.log(e);
      this.globalStore.notificationStore.showErrorNotification({
        message: 'An error occurred while saving payoff adjustments.',
      });
    }
  }

  *downloadCalculatorPDF() {
    try {
      const date = this.isPrePayoffCalculator ? this.dateForPrePayoffCalculator : null;
      const payoffId = this.isPrePayoffCalculator ? null : this.documentStore?.currentDocument?.payoffId;
      const response = yield this.payoffService.downloadPayoffStatementPdf(
        this.loanStore?.loanDetails?.loanId, payoffId, date
      );
      downloadDocument(response?.data, response?.headers, 'download');
    } catch (e) {
      console.log(e);
      this.globalStore.notificationStore.showErrorNotification({
        message: 'An error occurred while downloading payoff calculator pdf.',
      });
    }
  }

  *payoffRecalculate() {
    try {
      const response = yield this.payoffService.recalculateRevenueDataForLoanV2(this.loanStore?.loanDetails?.loanId);
      yield this.getPayoffCalculatorData();
      this.globalStore.notificationStore.showSuccessNotification({
        message: 'Payoff recalculation done successfully.',
      });
      this.setIsConfirmReCalculateDialogOpen(false);
    } catch (e) {
      console.log(e);
      this.globalStore.notificationStore.showErrorNotification({
        message: 'An error occurred while recalculating payoff.',
      });
    }
  }

  *downloadPayoffExcel(type: string) {
    try {
      const date = this.isPrePayoffCalculator ? this.dateForPrePayoffCalculator : null;
      const payoffId = this.isPrePayoffCalculator ? null : this.documentStore?.currentDocument?.payoffId;
      const response = yield this.payoffService.downloadPayoffExcel(
        type, this.loanStore?.loanDetails?.loanId, payoffId, date
      );
      downloadDocument(response?.data, response?.headers, 'download');
    } catch (e) {
      console.log(e);
      this.globalStore.notificationStore.showErrorNotification({
        message: 'An error occurred while downloading payoff excel.',
      });
    }
  }

  setIsPrePayoffCalculator(isPrePayoffCalculator: boolean) {
    this.isPrePayoffCalculator = isPrePayoffCalculator;
  }

  setDateForPrePayoffCalculator(dateForPrePayoffCalculator: string) {
    this.dateForPrePayoffCalculator = dateForPrePayoffCalculator;
  }
}

export default PayoffCalculatorStore;